import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function requestCode() {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data.message); setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function submit(e) {
    e.preventDefault();
    if (!sent) return requestCode();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { email, code, password });
      localStorage.removeItem("token");
      toast.success("Password reset. Sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  }

  return <div className="min-h-screen flex items-center justify-center p-6">
    <form onSubmit={submit} className="w-full max-w-sm space-y-5">
      <h1 className="font-heading font-black text-3xl">Reset your password</h1>
      <p className="text-sm text-muted-foreground">{sent ? "Enter your email code and choose a new password. Codes expire in 10 minutes." : "Enter your account email to request a reset code. Google-only accounts should continue with Google."}</p>
      <div className="space-y-2">
        <Label htmlFor="reset-email">Email</Label>
        <Input id="reset-email" type="email" autoComplete="email" required readOnly={sent} value={email} onChange={e => setEmail(e.target.value)} data-testid="reset-email" />
      </div>
      {sent && <>
        <div className="space-y-2"><Label htmlFor="reset-code">Reset code</Label>
          <Input id="reset-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} data-testid="reset-code" /></div>
        <div className="space-y-2"><Label htmlFor="reset-password">New password (at least 8 characters)</Label>
          <Input id="reset-password" type="password" autoComplete="new-password" minLength={8} maxLength={72} required value={password} onChange={e => setPassword(e.target.value)} data-testid="reset-password" /></div>
        <div className="space-y-2"><Label htmlFor="reset-confirm">Confirm password</Label>
          <Input id="reset-confirm" type="password" autoComplete="new-password" required value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="reset-confirm" /></div>
      </>}
      {message && <p role="status" className="text-sm" data-testid="reset-message">{message}</p>}
      {error && <p role="alert" className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
      <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="reset-submit">{busy ? "Please wait..." : sent ? "Reset password" : "Send reset code"}</Button>
      {sent && <>
        <Button type="button" variant="ghost" disabled={busy} onClick={requestCode} data-testid="reset-resend">Resend code (wait 60 seconds between requests)</Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={() => { setSent(false); setCode(""); setMessage(""); setError(""); }} data-testid="reset-change-email">Change email</Button>
      </>}
      <Link to="/login" className="block text-center text-primary" data-testid="reset-back-login">Back to sign in</Link>
    </form>
  </div>;
}
